import { Button, Icon, makeStyles, Theme } from '@material-ui/core';
import { capitalizeAll } from '@moderepo/mode-app-components';
import { ProjectDevice } from '@moderepo/mode-internal-apis';
import clsx from 'clsx';
import { useCallback, useMemo } from 'react';
import { DEFAULT_DEVICE_CONFIG_FILENAME, RegisterDeviceStepProps, useDeviceRegistrationWizardStyle } from '.';
import { useRitzTranslation } from '../../../utils/customHooks';
import { useSelectProject } from '../../../utils/dataStateHooks';


const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
        },

        downloadButtonContainer: {
            padding  : theme.spacing(2, 0),
            textAlign: 'center',
        },

        downloadButton: {
            textTransform: 'none',
        },
    };
});



/**
 * The required props for the DeviceRegistrationDownloadDeviceConfigStep in addition to the props inherited from RegisterDeviceStep
 */
export interface DeviceRegistrationDownloadDeviceConfigStepProps extends RegisterDeviceStepProps {
    readonly targetDevice: ProjectDevice;
    readonly onStepCompleted: ()=> void;
}


/**
 * This is the Download Device Config step
 */
export const DeviceRegistrationDownloadDeviceConfigStep: React.FC<DeviceRegistrationDownloadDeviceConfigStepProps> = (
    props: DeviceRegistrationDownloadDeviceConfigStepProps,
) => {

    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const sharedClasses = useDeviceRegistrationWizardStyle();
    const classes = useStyle();



    // Find the deviceClassSettings from the "deviceRegistrationWizardSettings" that is configured for the targetDevice
    const selectedDeviceClassesSettings = useMemo(() => {
        return props.deviceRegistrationWizardSettings.deviceClassesSettings.find((deviceClassSettings) => {
            return deviceClassSettings.deviceClass === props.targetDevice.deviceClass;
        });
    }, [props.deviceRegistrationWizardSettings.deviceClassesSettings, props.targetDevice.deviceClass]);


    const configFileName = useMemo(() => {
        return selectedDeviceClassesSettings?.deviceConfigFileName ?? DEFAULT_DEVICE_CONFIG_FILENAME;
    }, [selectedDeviceClassesSettings?.deviceConfigFileName]);


    const onDownloadLinkClick = useCallback(() => {
        if (selectedDeviceClassesSettings) {

            // Get the device config template as STRING. If the config is an Object then convert it to string.
            // It is easier to do search/replace placeholders if the whole thing is a string
            const templateString = JSON.stringify(selectedDeviceClassesSettings.deviceConfigTemplate);

            // Replace the placeholders with the device's values
            const content = templateString
                .replaceAll(/"\s*{{\s*_deviceId_\s*}}\s*"/g, props.targetDevice.id.toString())
                .replaceAll(/{{\s*_deviceToken_\s*}}/g, props.targetDevice.apiKey);

            // Format the content string into a JSON format. This will be the format we will write to the file.
            const jsonFormattedContent = JSON.stringify(JSON.parse(content), undefined, 4);

            const blob = new Blob([jsonFormattedContent], { type: 'text/octet-stream' });
            const anchor = document.createElement('A') as HTMLAnchorElement;
            anchor.download = configFileName;
            anchor.setAttribute('target', 'blank');
            anchor.setAttribute('href', window.URL.createObjectURL(blob));
            anchor.click();
        }
    }, [configFileName, props.targetDevice.apiKey, props.targetDevice.id, selectedDeviceClassesSettings]);



    return (
        <div className={sharedClasses.stepContentBody}>
            <div className={clsx(sharedClasses.stepContentForm)}>
                <div className={clsx(sharedClasses.stepSubtitle)}>
                    {trans('device_registration_wizard.step_download_device_config.subtitle')}
                </div>
                <div className={classes.downloadButtonContainer}>
                    <Button
                        className={classes.downloadButton}
                        variant="contained"
                        color="primary"
                        onClick={onDownloadLinkClick}
                    >
                        {
                            trans('device_registration_wizard.step_download_device_config.download_link', {
                                file_name: configFileName,
                            })
                        }
                    </Button>
                </div>
            </div>

            <div className={sharedClasses.stepContentActionsContainer}>
                <div className={sharedClasses.stepContentRightActionsContainer}>
                    <Button
                        disabled={!props.canGoBack}
                        onClick={() => {
                            // Validate the inputs AND dispatch onComplete if successful so that we can save the user inputs before going back.
                            // However, if inputs are invalid, we will not dispatch error and just take the user back to the previous step. We will
                            // not save the user's input since it is invalid anyway.
                            props.onStepError(false);
                            props.onPrevStepClick();
                        }}
                        className={sharedClasses.navButton}
                        startIcon={<Icon>navigate_before</Icon>}
                    >
                        {capitalizeAll(trans('label.back'))}
                    </Button>
                    <Button
                        onClick={() => {
                            props.onNextStepClick();
                        }}
                        className={sharedClasses.navButton}
                        endIcon={<Icon>navigate_next</Icon>}
                    >
                        {capitalizeAll(trans(props.isLastStep ? 'label.finish' : 'label.next'))}
                    </Button>
                </div>
            </div>
        </div>
    );
};
